import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  computed,
  inject,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { catchError, forkJoin, map, of, Subscription } from 'rxjs';
import { Permissions } from '@app/core/guard/Permissions';
import { PermissionsService } from '@services/helpers/permissions.service';
import { RealtimeService } from '@services/realtime/realtime.service';
import { RfqChanges } from '@interfaces/realtime/events';
import {
  rfqChanged$,
  rfqFallbackPoll$,
} from '@features/dashboard/control/risk-management/monitoring/pages/rfqs/helpers/rfq-realtime-refresh';
import {
  RfqEmailAttachmentResponse,
  RfqEmailResponse,
} from '@interfaces/risk-monitoring/responses/rfq-email.response';
import {
  RfqEmailQuoteAnalysisResponse,
  RfqExtractedQuoteResponse,
  RfqItemResponse,
  RfqResponse,
} from '@interfaces/risk-monitoring/responses/rfq.response';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { buildRfqQuoteInsight, RfqQuoteInsight } from './rfq-quote-insight.util';
import { RiskMonitoringHttpService } from '@services/http/risk-monitoring/risk-monitoring-http.service';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { SharedModule } from '@shared/shared.module';

interface RfqDetailField {
  label: string;
  value: string | number | null | undefined;
  type: 'text' | 'number';
  digits?: number;
}

interface RfqAiQuoteRow {
  key: string;
  analysis: RfqEmailQuoteAnalysisResponse;
  item: RfqItemResponse | null;
  quote: RfqExtractedQuoteResponse | null;
  fields: RfqDetailField[];
  /** Leitura gerencial da cotação, mostrada no (i) da linha. */
  insight: RfqQuoteInsight;
}

@Component({
  selector: 'app-modal-rfq-emails',
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: './modal-rfq-emails.component.html',
  styleUrls: ['./modal-rfq-emails.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalRfqEmailsComponent implements OnDestroy {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;
  @ViewChild('detailSection') detailSectionRef?: ElementRef<HTMLElement>;
  @ViewChild('insightPop') insightPopRef?: ElementRef<HTMLElement>;

  readonly dialogKey = 'risk-monitoring-rfq-emails';
  private readonly destroyRef = inject(DestroyRef);
  private readonly dateFormatter = inject(DateFormatterService);
  private readonly realtime = inject(RealtimeService);
  /**
   * Card 3902: o tratamento do HTML do e-mail (`POST rfqs/emails/process-html`) pede `Rfq.PROCESS_QUOTES`. Sem ela a
   * modal mostra o e-mail como chegou, sem chamar a API ao abrir.
   */
  private readonly canProcessQuotes = toSignal(
    inject(PermissionsService).hasPermission(Permissions.Rfq.PROCESS_QUOTES).pipe(map(Boolean)),
    { initialValue: false }
  );
  /** Polling sem tempo real conectado (o de antes do card 3588); conectado, cai para 60 s. */
  private readonly detailsPollingIntervalMs = 15000;
  /** Respostas que a IA terminou de ler sem gerar análise (`analysis-completed`); param o polling. */
  private settledEmailIds = new Set<string>();

  currentRfq = signal<RfqResponse | null>(null);
  emails = signal<RfqEmailResponse[]>([]);
  /** E-mail pedido no `open` para abrir já selecionado; vale até a primeira seleção. */
  private preferredEmailId: string | null = null;
  selectedEmail = signal<RfqEmailResponse | null>(null);
  selectedEmailIframeSrc = signal<SafeResourceUrl | null>(null);
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  isHeaderExpanded = signal(false);
  isAiPanelExpanded = signal(false);
  filterDirection = signal<'All' | 'Inbound' | 'Outbound'>('All');
  showAttachments = signal(false);
  /** Lista de e-mails recolhida: a leitura do e-mail ocupa a largura toda da modal (card 3301). */
  isListCollapsed = signal(false);
  /**
   * Popover da leitura gerencial do (i) da linha (card 3545). Mora dentro da seção de detalhe, com
   * posição calculada a partir do ícone: o overlay do PrimeNG some dentro de modal (#2798) e um
   * popover dentro da tabela seria cortado pela rolagem do painel.
   */
  insightPopover = signal<{
    key: string;
    insight: RfqQuoteInsight;
    right: number;
    top: number | null;
    bottom: number | null;
  } | null>(null);
  /** Linha cujo popover foi fixado por clique (toque); hover e blur não o fecham. */
  insightPinnedKey = signal<string | null>(null);
  private insightHideTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly boundHandleClickOutside = this.handleClickOutside.bind(this);
  private processedHtmlCache = new Map<string, string>();
  private pollingSubscription: Subscription | null = null;
  private lastRfqSnapshot = '';
  private lastEmailsSnapshot = '';

  analysesForSelectedEmail = computed<RfqEmailQuoteAnalysisResponse[]>(() => {
    const email = this.selectedEmail();
    const rfq = this.currentRfq();
    if (!email || !rfq || email.direction !== 'Inbound') return [];
    return rfq.inboundEmailAnalyses?.filter(a => a.rfqEmailId === email.id) ?? [];
  });

  /**
   * Uma linha por cotação extraída, em ordem de vencimento, para a tabela compacta do painel da IA
   * (card 3545). Análise sem cotação estruturada vira uma linha com `quote` nulo.
   */
  aiQuoteRows = computed<RfqAiQuoteRow[]>(() => {
    const bankName = this.selectedEmail()?.bankName;
    const dateFormat = this.dateFormatter.getUserDateFormat() || 'dd/MM/yyyy';
    const formatDate = (date: string) => this.dateFormatter.formatDateInFormat(date, dateFormat);
    const maturityTime = (row: RfqAiQuoteRow) =>
      this.dateFormatter.parseDate(row.item?.maturityDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const rows = this.analysesForSelectedEmail().flatMap<RfqAiQuoteRow>(analysis => {
      const item = this.getItemForAnalysis(analysis.itemId);
      const quotes = analysis.hasExtractedQuotes ? analysis.quotes : [];
      const insightFor = (quote: RfqExtractedQuoteResponse | null) => {
        const insight = buildRfqQuoteInsight(
          analysis,
          item,
          quote,
          bankName,
          this.formatInstrumentKind(item?.kind ?? quote?.kind),
          formatDate
        );
        return { insight };
      };
      if (!quotes.length) {
        return [
          { key: analysis.itemId, analysis, item, quote: null, fields: [], ...insightFor(null) },
        ];
      }
      return quotes.map((quote, index) => ({
        key: `${analysis.itemId}-${index}`,
        analysis,
        item,
        quote,
        fields: this.getQuoteFields(quote),
        ...insightFor(quote),
      }));
    });

    return rows.sort((a, b) => maturityTime(a) - maturityTime(b));
  });

  /** Resumo na barra recolhida: tipos e a faixa de cada campo cotado (sem o spot), ex. "Forward 5,6206 a 5,9590". */
  aiQuoteSummary = computed<string>(() => {
    const rows = this.aiQuoteRows();
    if (!rows.length) return '';

    const kinds = Array.from(
      new Set(rows.map(r => this.formatInstrumentKind(r.item?.kind ?? r.quote?.kind)))
    );
    const ranges = new Map<string, { min: number; max: number; digits: number }>();
    for (const row of rows) {
      for (const field of row.fields) {
        if (field.label === 'Spot' || typeof field.value !== 'number') continue;
        const range = ranges.get(field.label);
        if (range) {
          range.min = Math.min(range.min, field.value);
          range.max = Math.max(range.max, field.value);
        } else {
          ranges.set(field.label, {
            min: field.value,
            max: field.value,
            digits: field.digits ?? 2,
          });
        }
      }
    }

    const format = (value: number, digits: number) =>
      value.toLocaleString('pt-BR', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
    const rangeLabels = Array.from(ranges.entries())
      .slice(0, 3)
      .map(([label, { min, max, digits }]) =>
        min === max
          ? `${label} ${format(min, digits)}`
          : `${label} ${format(min, digits)} a ${format(max, digits)}`
      );

    const missing = rows.filter(r => !r.quote).length;
    const missingLabel = missing ? [`${missing} sem cotação`] : [];
    return [kinds.join(', '), ...rangeLabels, ...missingLabel].join(' · ');
  });

  filteredEmails = computed(() => {
    const dir = this.filterDirection();
    const list = this.emails();
    if (dir === 'All') return list;
    return list.filter(e => e.direction === dir);
  });

  private blobUrls: string[] = [];

  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly cdr: ChangeDetectorRef,
    private readonly riskMonitoringHttpService: RiskMonitoringHttpService,
    private readonly toastHelperService: ToastHelperService
  ) {
    // Tempo real (card 3588): resposta nova do banco aparece na hora, sem esperar o polling.
    rfqChanged$(this.realtime, payload => {
      if (payload.rfqId !== this.currentRfq()?.id) return false;
      if (payload.change === RfqChanges.AnalysisCompleted && payload.rfqEmailId) {
        this.settledEmailIds.add(payload.rfqEmailId);
      }
      // Reprocessar a IA apaga as análises: as respostas voltam a esperar a leitura nova.
      if (payload.change === RfqChanges.AiRerun) this.settledEmailIds.clear();
      return true;
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const currentRfq = this.currentRfq();
        if (currentRfq) this.loadEmailHistory(currentRfq, false);
      });
  }

  ngOnDestroy(): void {
    this.cancelInsightHide();
    this.clearBlobUrls();
    this.processedHtmlCache.clear();
    document.removeEventListener('click', this.boundHandleClickOutside, true);
  }

  /**
   * @param preferredEmailId e-mail que já abre selecionado (ex.: "Ver e-mail completo" da modal de
   * limites informados pelo banco, card 3787); sem ele, abre no primeiro.
   */
  open(rfq: RfqResponse, preferredEmailId: string | null = null): void {
    this.preferredEmailId = preferredEmailId;
    this.currentRfq.set(rfq);
    this.emails.set([]);
    this.selectedEmail.set(null);
    this.selectedEmailIframeSrc.set(null);
    this.errorMessage.set(null);
    this.isLoading.set(true);
    this.isHeaderExpanded.set(false);
    this.isListCollapsed.set(false);
    this.filterDirection.set('All');
    this.clearBlobUrls();
    this.processedHtmlCache.clear();
    this.lastRfqSnapshot = '';
    this.lastEmailsSnapshot = '';
    this.settledEmailIds = new Set();

    this.dialogRef?.open('', '', '', '', '', this.dialogKey, false, false);

    this.stopPolling();
    this.loadEmailHistory(rfq, true);
  }

  onDialogClosed(): void {
    this.stopPolling();
    this.currentRfq.set(null);
    this.emails.set([]);
    this.selectedEmail.set(null);
    this.selectedEmailIframeSrc.set(null);
    this.isLoading.set(false);
    this.errorMessage.set(null);
    this.isHeaderExpanded.set(false);
    this.closeAttachments();
    this.clearBlobUrls();
    this.processedHtmlCache.clear();
    this.lastRfqSnapshot = '';
    this.lastEmailsSnapshot = '';
  }

  selectEmail(email: RfqEmailResponse, preserveUiState = false): void {
    const isSameEmailSelected = this.selectedEmail()?.id === email.id;
    const cached = this.processedHtmlCache.get(email.id);
    const nextHtml = cached ?? email.latestHtml ?? email.originalHtml;
    const currentHtml = this.getSelectedEmailHtmlContent();

    this.selectedEmail.set(email);

    if (!isSameEmailSelected || currentHtml !== nextHtml) {
      this.selectedEmailIframeSrc.set(this.buildEmailIframeSrc(nextHtml));
    }

    if (!preserveUiState || !isSameEmailSelected) {
      this.isHeaderExpanded.set(false);
      // Cotações extraídas começam recolhidas: o corpo do e-mail do banco é o que se lê primeiro (card 3545).
      this.isAiPanelExpanded.set(false);
      this.hideInsightNow();
      this.closeAttachments();
    }

    if (!cached && this.canProcessQuotes()) {
      this.processEmailHtml(email);
    }
  }

  getEmailIframeSrc(htmlContent: string): SafeResourceUrl {
    this.clearBlobUrls();
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    this.blobUrls.push(url);
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  getProcessedHtml(email: RfqEmailResponse): string {
    return (
      this.processedHtmlCache.get(email.id) ??
      email.latestHtml ??
      email.originalHtml ??
      '<html><body></body></html>'
    );
  }

  toggleHeader(): void {
    this.isHeaderExpanded.update(v => !v);
  }

  /** Recolhe ou mostra a lista de e-mails, para ler a resposta do banco com a largura toda da modal. */
  toggleList(): void {
    this.isListCollapsed.update(v => !v);
  }

  toggleAttachments(): void {
    this.showAttachments.update(v => !v);
    if (this.showAttachments()) {
      setTimeout(() => document.addEventListener('click', this.boundHandleClickOutside, true), 0);
    } else {
      document.removeEventListener('click', this.boundHandleClickOutside, true);
    }
  }

  closeAttachments(): void {
    this.showAttachments.set(false);
    document.removeEventListener('click', this.boundHandleClickOutside, true);
  }

  private handleClickOutside(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.rfq-emails-modal__attachments-inline')) {
      this.closeAttachments();
    }
  }

  setFilter(direction: 'All' | 'Inbound' | 'Outbound'): void {
    this.filterDirection.set(direction);
  }

  openAttachment(attachment: RfqEmailAttachmentResponse): void {
    if (!attachment.blobUrl) {
      this.toastHelperService.showToastWarn(
        'Anexo indisponível',
        'Não foi possível gerar a URL temporária deste anexo.'
      );
      return;
    }

    window.open(attachment.blobUrl, '_blank', 'noopener,noreferrer');
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileIcon(filename: string): string {
    const extension = filename.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return 'pi pi-file-pdf';
      case 'doc':
      case 'docx':
        return 'pi pi-file-word';
      case 'xls':
      case 'xlsx':
        return 'pi pi-file-excel';
      case 'ppt':
      case 'pptx':
        return 'pi pi-file';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'svg':
        return 'pi pi-image';
      case 'zip':
      case 'rar':
      case '7z':
        return 'pi pi-folder';
      default:
        return 'pi pi-file';
    }
  }

  getDownloadableAttachments(email: RfqEmailResponse): RfqEmailAttachmentResponse[] {
    return email.attachments.filter(a => !a.isInline);
  }

  getDirectionLabel(direction?: string | null): string {
    return direction === 'Inbound' ? 'Recebido' : 'Enviado';
  }

  getSidebarCounterpartyLabel(email: RfqEmailResponse): string {
    const isOutbound = email.direction === 'Outbound';
    const primaryEmail = isOutbound ? email.to?.[0] : email.from;
    const primaryName = isOutbound ? email.bankManagerName : email.repliedFromName;
    const parts = [primaryName?.trim(), primaryEmail?.trim()].filter(
      (value, index, allValues) => !!value && allValues.indexOf(value) === index
    ) as string[];
    const extraRecipientsCount = isOutbound ? Math.max((email.to?.length ?? 0) - 1, 0) : 0;
    const baseLabel = parts.length ? parts.join(' · ') : '-';
    const suffix = extraRecipientsCount > 0 ? ` +${extraRecipientsCount}` : '';

    return `${isOutbound ? 'Para' : 'De'}: ${baseLabel}${suffix}`;
  }

  getParticipantList(values?: string[] | null): string {
    if (!values?.length) return '-';
    return values.join(', ');
  }

  getInstrumentCountLabel(): string {
    const count = this.getRfqItems().length;
    return count === 1 ? '1 instrumento' : `${count} instrumentos`;
  }

  getInstrumentTypesLabel(): string {
    const labels = Array.from(
      new Set(this.getRfqItems().map(item => this.formatInstrumentKind(item.kind)))
    );

    return labels.length ? labels.join(', ') : '-';
  }

  emailHasAnalyses(emailId: string): boolean {
    return (this.currentRfq()?.inboundEmailAnalyses ?? []).some(
      a => a.rfqEmailId === emailId && a.hasExtractedQuotes
    );
  }

  toggleAiPanel(): void {
    this.isAiPanelExpanded.update(v => !v);
    this.hideInsightNow();
  }

  /** Abre o popover ao lado do (i); na metade de baixo da seção, abre para cima. */
  showInsight(event: Event, row: RfqAiQuoteRow): void {
    this.cancelInsightHide();
    // Fixado por clique, o popover não troca de linha só porque o mouse passou por outro (i).
    const pinned = this.insightPinnedKey();
    if (pinned && pinned !== row.key) return;
    const container = this.detailSectionRef?.nativeElement;
    const icon = event.currentTarget as HTMLElement | null;
    if (!container || !icon) return;

    const box = container.getBoundingClientRect();
    const anchor = icon.getBoundingClientRect();
    const right = Math.max(8, box.right - anchor.left + 8);
    const opensUp = anchor.top - box.top > box.height / 2;

    this.insightPopover.set({
      key: row.key,
      insight: row.insight,
      right,
      top: opensUp ? null : Math.max(8, anchor.top - box.top - 8),
      bottom: opensUp ? Math.max(8, box.bottom - anchor.bottom - 8) : null,
    });
    requestAnimationFrame(() => this.fitInsightPopover());
  }

  /**
   * Depois de desenhado, empurra o popover para dentro da seção quando a altura real não cabe
   * (modal baixa, tela pequena): a escolha cima/baixo pelo ícone não basta, porque a altura do
   * texto varia por instrumento. Mais alto que a seção, ele ganha rolagem própria (max-height no CSS).
   */
  private fitInsightPopover(): void {
    const container = this.detailSectionRef?.nativeElement;
    const pop = this.insightPopRef?.nativeElement;
    const current = this.insightPopover();
    if (!container || !pop || !current) return;

    const box = container.getBoundingClientRect();
    const rect = pop.getBoundingClientRect();
    const margin = 8;
    if (rect.top >= box.top + margin && rect.bottom <= box.bottom - margin) return;

    const top = Math.max(margin, Math.min(rect.top - box.top, box.height - rect.height - margin));
    this.insightPopover.set({ ...current, top, bottom: null });
  }

  /**
   * Clique no (i) fixa o popover (toque não tem hover; o mouseenter/focus que vêm antes do clique
   * já o abriram, então o clique não pode fechar); clicar de novo no mesmo (i) solta e fecha.
   */
  toggleInsight(event: Event, row: RfqAiQuoteRow): void {
    if (this.insightPinnedKey() === row.key) {
      this.hideInsightNow();
      return;
    }
    this.insightPinnedKey.set(null);
    this.showInsight(event, row);
    this.insightPinnedKey.set(row.key);
  }

  /** Fecha com um respiro, para o mouse poder ir do (i) até o popover sem ele sumir. Fixado, fica. */
  scheduleInsightHide(): void {
    this.cancelInsightHide();
    if (this.insightPinnedKey()) return;
    this.insightHideTimer = setTimeout(() => this.insightPopover.set(null), 150);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.insightPopover()) this.hideInsightNow();
  }

  cancelInsightHide(): void {
    if (this.insightHideTimer) {
      clearTimeout(this.insightHideTimer);
      this.insightHideTimer = null;
    }
  }

  hideInsightNow(): void {
    this.cancelInsightHide();
    this.insightPinnedKey.set(null);
    this.insightPopover.set(null);
  }

  getItemForAnalysis(itemId: string): RfqItemResponse | null {
    return this.getRfqItems().find(i => i.itemId === itemId) ?? null;
  }

  getQuoteFields(quote: RfqExtractedQuoteResponse): RfqDetailField[] {
    const spotField: RfqDetailField = {
      label: 'Spot',
      value: quote.spotRate ?? 0,
      type: 'number',
      digits: 4,
    };

    switch (quote.kind) {
      case 'NdfVanilla':
        return [spotField, { label: 'Forward', value: quote.forward, type: 'number', digits: 4 }];
      case 'NdfCap':
        return [
          spotField,
          { label: 'Forward', value: quote.forward, type: 'number', digits: 4 },
          { label: 'CAP', value: quote.cap, type: 'number', digits: 4 },
        ];
      case 'CallVanilla':
        return [
          spotField,
          { label: 'Strike', value: quote.strike, type: 'number', digits: 4 },
          { label: 'Prêmio', value: quote.premium, type: 'number', digits: 4 },
        ];
      case 'CallSpread':
        return [
          spotField,
          { label: 'Strike inf.', value: quote.lowStrike, type: 'number', digits: 4 },
          { label: 'Strike sup.', value: quote.highStrike, type: 'number', digits: 4 },
          { label: 'Prêmio', value: quote.premium, type: 'number', digits: 4 },
        ];
      case 'PutVanilla':
        return [
          spotField,
          { label: 'Strike', value: quote.strike, type: 'number', digits: 4 },
          { label: 'Prêmio', value: quote.premium, type: 'number', digits: 4 },
        ];
      case 'PutSpread':
        return [
          spotField,
          { label: 'Strike inf.', value: quote.lowStrike, type: 'number', digits: 4 },
          { label: 'Strike sup.', value: quote.highStrike, type: 'number', digits: 4 },
          { label: 'Prêmio', value: quote.premium, type: 'number', digits: 4 },
        ];
      case 'Collar':
        return [
          spotField,
          { label: 'Strike Put (vend.)', value: quote.lowStrike, type: 'number', digits: 4 },
          { label: 'Strike Call (compr.)', value: quote.highStrike, type: 'number', digits: 4 },
          { label: 'Prêmio', value: quote.premium, type: 'number', digits: 4 },
        ];
      case 'Loan4131':
        return [
          { label: 'Indexador', value: quote.indexer, type: 'text' },
          { label: 'Spread (% a.a.)', value: quote.spreadPercentage, type: 'number', digits: 4 },
          { label: 'All-in (% a.a.)', value: quote.allInRatePercentage, type: 'number', digits: 4 },
          { label: 'Condições', value: quote.conditions, type: 'text' },
        ];
      default:
        return [];
    }
  }

  private processEmailHtml(email: RfqEmailResponse): void {
    const htmlToProcess = email.originalHtml ?? email.latestHtml;
    if (!htmlToProcess) return;

    this.riskMonitoringHttpService.processRfqEmailHtml(email.id, htmlToProcess).subscribe({
      next: processedHtml => {
        if (processedHtml) {
          this.processedHtmlCache.set(email.id, processedHtml);
          if (this.selectedEmail()?.id === email.id) {
            this.selectedEmailIframeSrc.set(this.buildEmailIframeSrc(processedHtml));
            this.cdr.detectChanges();
          }
        }
      },
      error: err => {
        console.warn('Failed to process RFQ email HTML', err);
      },
    });
  }

  private clearBlobUrls(): void {
    for (const url of this.blobUrls) {
      URL.revokeObjectURL(url);
    }

    this.blobUrls = [];
  }

  private buildEmailIframeSrc(html?: string | null): SafeResourceUrl | null {
    this.clearBlobUrls();

    const content = this.withReaderStyles(html || '<html><body></body></html>');
    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    this.blobUrls.push(url);

    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  /**
   * Estilo de leitura acrescentado ao HTML do e-mail antes de ir para o iframe (card 3545), sem
   * mexer no conteúdo do banco:
   * - o Outlook reescreve o `body { background: #fff }` do nosso e-mail citado na resposta como
   *   `div { background: #fff }`, que pinta de branco os `div` das células do cabeçalho da tabela
   *   do banco (texto branco em fundo navy) e o cabeçalho some (RFQ 46, Bradesco);
   * - aviso legal em `<pre>` sem quebra deixava o e-mail com 5.000px de largura.
   */
  private withReaderStyles(html: string): string {
    const style = `<style data-cognito-reader>
      td div:not([style*="background"]), th div:not([style*="background"]) { background-color: transparent !important; }
      pre { white-space: pre-wrap !important; word-break: break-word; }
    </style>`;
    const headClose = html.search(/<\/head>/i);
    return headClose >= 0 ? html.slice(0, headClose) + style + html.slice(headClose) : style + html;
  }

  formatInstrumentKind(kind?: string | null): string {
    switch (kind) {
      case 'NdfVanilla':
        return 'NDF Vanilla';
      case 'NdfCap':
        return 'NDF com CAP';
      case 'CallSpread':
        return 'Call Spread';
      case 'CallVanilla':
        return 'Call Vanilla';
      case 'PutVanilla':
        return 'Put Vanilla';
      case 'PutSpread':
        return 'Put Spread';
      case 'Loan4131':
        return 'Empréstimo 4131';
      case 'LimitUpdate':
        return 'Atualização de limites';
      default:
        return kind || 'Derivativo';
    }
  }

  private getRfqItems(): RfqResponse['items'] {
    const rfq = this.currentRfq();
    return rfq?.items ?? rfq?.instruments ?? [];
  }

  private loadEmailHistory(baseRfq: RfqResponse, showLoadingState: boolean): void {
    if (showLoadingState) {
      this.isLoading.set(true);
    }

    forkJoin({
      rfq: this.riskMonitoringHttpService.getRfqById(baseRfq.id).pipe(catchError(() => of(null))),
      emails: this.riskMonitoringHttpService
        .getRfqEmails(baseRfq.sequentialId)
        .pipe(catchError(() => of([] as RfqEmailResponse[]))),
    }).subscribe({
      next: ({ rfq, emails }) => {
        const nextRfq = rfq ?? this.currentRfq() ?? baseRfq;
        const emailList = emails ?? [];
        const nextRfqSnapshot = this.buildSnapshot(nextRfq);
        const nextEmailsSnapshot = this.buildSnapshot(emailList);
        const hasRfqChanges = nextRfqSnapshot !== this.lastRfqSnapshot;
        const hasEmailChanges = nextEmailsSnapshot !== this.lastEmailsSnapshot;
        const shouldApplyData = showLoadingState || hasRfqChanges || hasEmailChanges;
        const previousSelectedId = this.selectedEmail()?.id;

        if (shouldApplyData) {
          const wantedId = previousSelectedId ?? this.preferredEmailId;
          const nextSelectedEmail =
            emailList.find(email => email.id === wantedId) ?? emailList[0] ?? null;

          this.currentRfq.set(nextRfq);
          this.emails.set(emailList);
          this.lastRfqSnapshot = nextRfqSnapshot;
          this.lastEmailsSnapshot = nextEmailsSnapshot;

          if (nextSelectedEmail) {
            this.selectEmail(nextSelectedEmail, !showLoadingState);
          } else {
            this.selectedEmail.set(null);
            this.selectedEmailIframeSrc.set(null);
            this.isAiPanelExpanded.set(false);
            this.closeAttachments();
          }
        }

        if (showLoadingState) {
          this.isLoading.set(false);
          this.errorMessage.set(null);
        } else if (rfq) {
          this.errorMessage.set(null);
        }

        this.syncPollingWithCurrentState();
      },
      error: () => {
        if (showLoadingState) {
          this.emails.set([]);
          this.selectedEmail.set(null);
          this.selectedEmailIframeSrc.set(null);
          this.isLoading.set(false);
          this.errorMessage.set(
            'Não foi possível carregar o histórico completo de emails desta RFQ.'
          );
        }
      },
    });
  }

  private syncPollingWithCurrentState(): void {
    const currentRfq = this.currentRfq();
    if (!currentRfq || !this.shouldPoll(currentRfq)) {
      this.stopPolling();
      return;
    }

    if (this.pollingSubscription) {
      return;
    }

    this.pollingSubscription = rfqFallbackPoll$(this.realtime, this.detailsPollingIntervalMs)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const rfqToRefresh = this.currentRfq();
        if (!rfqToRefresh) {
          this.stopPolling();
          return;
        }

        if (!this.shouldPoll(rfqToRefresh)) {
          this.stopPolling();
          return;
        }

        this.loadEmailHistory(rfqToRefresh, false);
      });
  }

  private stopPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
  }

  private shouldPoll(rfq: RfqResponse): boolean {
    return this.hasOpenRfqStatus(rfq) || this.hasPendingInboundEmailAnalysis(rfq, this.emails());
  }

  private hasOpenRfqStatus(rfq: RfqResponse): boolean {
    return rfq.status === 'Sent' || rfq.status === 'PartiallyAnswered';
  }

  private hasPendingInboundEmailAnalysis(rfq: RfqResponse, emails: RfqEmailResponse[]): boolean {
    const inboundEmails = emails.filter(email => email.direction === 'Inbound');
    if (!inboundEmails.length) {
      return false;
    }

    const analysedEmailIds = new Set(
      (rfq.inboundEmailAnalyses ?? []).map(analysis => analysis.rfqEmailId)
    );
    return inboundEmails.some(
      email => !analysedEmailIds.has(email.id) && !this.settledEmailIds.has(email.id)
    );
  }

  private getSelectedEmailHtmlContent(): string | null {
    const selectedEmail = this.selectedEmail();
    if (!selectedEmail) {
      return null;
    }

    return (
      this.processedHtmlCache.get(selectedEmail.id) ??
      selectedEmail.latestHtml ??
      selectedEmail.originalHtml ??
      null
    );
  }

  private buildSnapshot(value: unknown): string {
    return JSON.stringify(value ?? null);
  }
}
